<template>
  <div class="game-task-rewards-root">
    <!-- 宝箱 -->
    <div
      class="gameback-rewards"
      role="button"
      tabindex="0"
      @click.stop="openTaskRewardsModal"
      @keydown.enter.prevent="openTaskRewardsModal"
    >
      <div class="gameback-rewards__chest-area">
        <div
          class="gameback-rewards__chest-wrap"
          :class="{
            'gameback-rewards__chest-wrap--hot': bannerChestHot,
            'gameback-rewards__chest-wrap--anim-ready': bannerAssetsReady,
          }"
        >
          <div class="gameback-rewards__glow-stack" aria-hidden="true">
            <img
              ref="bannerGlowBg"
              class="gameback-rewards__glow-bg"
              :src="giftBgPng"
              alt=""
              @load="onBannerAssetLoaded"
              @error="onBannerAssetLoaded"
            />
            <img
              ref="bannerGlowLight"
              class="gameback-rewards__glow-light"
              :src="giftLightPng"
              alt=""
              @load="onBannerAssetLoaded"
              @error="onBannerAssetLoaded"
            />
          </div>
          <img
            ref="bannerChest"
            class="gameback-rewards__chest"
            :class="{ 'gameback-rewards__chest--got': isGotTaskRewardsTheme }"
            :src="giftChestSrc"
            @load="onBannerAssetLoaded"
            @error="onBannerAssetLoaded"
          />
          <span v-if="bannerChestHot" class="gameback-rewards__red-dot" aria-hidden="true"></span>
        </div>
      </div>
      <div class="gameback-rewards__label" :data-text="$t('task.rewards')">
        {{ $t('task.rewards') }}
      </div>
    </div>

    <!-- 任务 -->
    <TaskRewardsFloatDialog
      :visible.sync="taskRewardsVisible"
      :title="dialogText.title"
      :close-on-click-overlay="true"
    >
      <template #float>
        <div class="reward-task">
          <div class="reward-task__chest-area" aria-hidden="true">
            <div class="reward-task__glow-stack">
              <img class="reward-task__glow-bg" :src="giftBgPng" alt="" />
              <img class="reward-task__glow-light" :src="giftLightPng" alt="" />
            </div>
            <img
              class="reward-task__chest"
              :class="{ 'reward-task__chest--got': isGotTaskRewardsTheme }"
              :src="giftChestSrc"
            />
          </div>
        </div>
      </template>
      <!-- —— 弹窗默认插槽：已登录任务表 + 游客/已登录底部 —— -->
      <div class="task-rewards-modal" :class="{ 'task-rewards-modal--hot': modalTaskHotAnim }">
        <div v-if="taskInfoLoading" class="row-content" @touchmove.stop v-touchmove>
          <div v-for="n in 2" :key="'task-sk-' + n" class="task-rewards-skeleton">
            <div class="skeleton-shimmer task-rewards-skeleton__title"></div>
            <div class="task-rewards-skeleton__bottom">
              <div class="skeleton-shimmer task-rewards-skeleton__prizes"></div>
              <div class="skeleton-shimmer task-rewards-skeleton__btn"></div>
            </div>
          </div>
        </div>
        <div v-else class="row-content" @touchmove.stop v-touchmove>
          <div
            v-for="(row, index) in taskRowsForModal"
            :key="row.task_key + index"
            class="task-rewards-row"
            :class="{ 'task-rewards-row--got': isGotTaskRewardsTheme }"
          >
            <div
              v-if="row.completed && taskRewardsVisible && modalTaskHotAnim"
              class="task-rewards-row__completeSweep"
              aria-hidden="true"
            >
              <div
                class="task-rewards-row__completeSweep-inner"
                :style="{ backgroundImage: `url(${taskSealCompletePng})` }"
              ></div>
            </div>
            <div class="task-rewards-row__main">
              <div
                v-if="row.completed"
                class="task-rewards-done__bg"
                :style="{ backgroundImage: `url(${completeTaskPng})` }"
              ></div>
              <div class="task-rewards-row__title">
                <span>{{ row.titleMain }}</span>
                <span
                  v-if="row.titleProgress && isLogin"
                  class="task-rewards-row__progress"
                  :class="{ 'task-rewards-row__progress--completed': row.completed }"
                  >{{ row.titleProgress }}</span
                >
              </div>
              <div class="bottom">
                <div
                  class="task-rewards-prizes"
                  :class="{
                    'task-rewards-prizes--got': isGotTaskRewardsTheme,
                    'task-rewards-prizes--wide': row.chips.length > 3,
                  }"
                  @mousedown="onPrizesDragStart"
                  @touchstart.passive="onPrizesTouchStart"
                  @touchmove.passive="onPrizesTouchMove"
                  @touchend="onPrizesTouchEnd"
                  @touchcancel="onPrizesTouchEnd"
                >
                  <span
                    v-for="(c, idx) in row.chips"
                    :key="row.task_key + '-' + idx"
                    class="task-rewards-chip"
                    :class="{ 'task-rewards-chip--icon': !!c.url }"
                    :style="c.url ? { backgroundImage: `url(${c.url})` } : {}"
                  >
                    <span class="task-rewards-chip__count" :data-text="c.count.toLocaleString()">{{
                      c.count.toLocaleString()
                    }}</span>
                  </span>
                </div>
                <div class="task-rewards-row__side">
                  <template>
                    <div
                      v-if="row.completed"
                      class="task-rewards-seal"
                      :class="{
                        'task-rewards-seal--play': modalTaskHotAnim,
                      }"
                    >
                      <div class="task-rewards-seal__stamp">
                        <div class="task-rewards-seal__text">
                          <div class="task-rewards-done__text">{{ dialogText.completedLbl }}</div>
                          <div class="task-rewards-done__date">{{ row.completedDate }}</div>
                        </div>
                      </div>
                    </div>
                    <div
                      v-else-if="shouldShowTaskRowGo(row.task_key)"
                      class="task-rewards-go medium"
                      @click="onTaskRowGo(row)"
                    >
                      {{ dialogText.goToBtn }}
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
        <template v-if="!taskLoggedIn">
          <p class="task-rewards-foot task-rewards-foot--guest">{{ dialogText.guestFoot }}</p>
          <button type="button" class="task-rewards-login-wide medium" @click="onTaskLoginGo">
            {{ $t('text.login') }}
          </button>
        </template>
        <p v-else class="task-rewards-foot">
          <span v-html="dialogText.footPlain"></span>
        </p>
      </div>
    </TaskRewardsFloatDialog>
  </div>
</template>

<script>
/**
 * Banner 任务奖励入口 + 弹窗：拉取 /task_info、展示进度与 PWA/登录引导。
 * 依赖父级 game.vue 传入 gameInfo、gameId；登录弹窗通过 $parent.setShowLoginModal。
 *
 * ── 文件结构索引（按大段，非逐行；改文件后请同步调整行号）──
 * · template（约 L1–L125）   game-task-rewards-root；Banner 宝箱；TaskRewardsFloatDialog + #float；
 *                            任务行 v-for；游客登录区 / 已登录脚注
 * · script（约 L127–L440）  import、组件选项、methods
 * · style scoped（约 L437+）弹窗内：任务卡片、扫光、印章、GO TO、底部登录宽按钮
 * · style 全局（约 L724+）   display:contents 根；Banner 宝箱动效；弹层上 reward-task 光效
 */
import { mapState } from 'vuex'
import dayjs from 'dayjs'
import { taskInfo } from '@/api/index.js'
import { isEnteredFromInstalledWebApp, isPwaTaskBeforeInstallProbeUa } from '@/tool/pwaEntry'
import taskSealCompletePng from '@/assets/image/complete.png'
import completeTaskPng from '@/assets/image/complete_task.png'
import giftPngDefault from '@/assets/image/gift.png'
import gotGiftPng from '@/assets/image/got_gift.png'
import giftBgPng from '@/assets/image/gift_bg.png'
import giftLightPng from '@/assets/image/gift_light.png'
import TaskRewardsFloatDialog from '@/components/common/TaskRewardsFloatDialog.vue'

export default {
  /** DevTools 中显示名 */
  name: 'GameTaskRewards',
  components: { TaskRewardsFloatDialog },
  props: {
    /** 当前游戏角色/展示信息（含 has_red_dot、show_entry、name 等），来自父级 game.vue */
    gameInfo: {
      type: Object,
      default: () => ({}),
    },
    /** 当前游戏 ID，请求 /task_info 时作为 game_id */
    gameId: { type: [String, Number], required: true },
  },
  /** 本地响应式状态：贴图资源、弹窗、任务列表、动效与 PWA 安装相关 */
  data() {
    return {
      /** 扫光动效覆盖层使用的「完成」贴图 */
      taskSealCompletePng,
      /** 未完成行背景装饰图 */
      completeTaskPng,
      /** 宝箱光效底层图 */
      giftBgPng,
      /** 宝箱光效旋转层图 */
      giftLightPng,
      /** 任务奖励弹窗显隐（与 Dialog :visible.sync 绑定） */
      taskRewardsVisible: false,
      /** 接口 POST /task_info 返回的 data.task_info 列表 */
      taskInfoList: [],
      /** /task_info 请求中，弹窗内展示骨架屏 */
      taskInfoLoading: false,
      /** 已点开过 Banner 宝箱：关闭 宝箱大幅度跳动/红点，不影响弹窗内动效 */
      bannerChestDismissed: false,
      /** 弹窗内扫光/印章等强动效：打开时有红点则开启，关闭弹窗后清除 */
      modalTaskHotAnim: false,
      /** Banner 宝箱与光效图全部加载完成后才开启动画 */
      bannerAssetsReady: false,
    }
  },
  mounted() {
    this.$nextTick(() => this.syncBannerAssetsReady())
  },
  /** 派生状态：Vuex 映射、Banner 动效、文案包、登录态、表格行数据 */
  computed: {
    /**
     * 从 Vuex 映射的只读状态
     * - isLogin：是否已登录
     * - isMobile：是否移动端
     * - accept：是否已同意 Cookie（控制登录前是否先弹 Cookie）
     * - gameScheme：游戏唤起 scheme（GO TO 跳转游戏）
     * - isInstallPromptSupported：与 head/webapp beforeinstallprompt 写入 store.app 同步
     */
    ...mapState({
      isLogin: (state) => state.user.isLogin,
      isMobile: (state) => state.app.isMobile,
      accept: (state) => state.app.accept,
      gameScheme: (state) => state.app.gameScheme,
      isInstallPromptSupported: (state) => state.app.isInstallPromptSupported,
    }),
    /** Banner 宝箱光效 + 红点：has_red_dot === 1 且本次会话尚未点开宝箱 */
    bannerChestHot() {
      return !this.bannerChestDismissed && Number(this.gameInfo.has_red_dot) === 1
    },
    /** GOT（kingshot / kingshot_ru）任务奖励区背景；GOF（wos / wos_ru）保持默认 #accbe7 */
    isGotTaskRewardsTheme() {
      const gn = process.env.VUE_APP_GAME_NAME
      const gid = String(process.env.VUE_APP_GAME_ID || '')
      return gn === 'kingshot' || gn === 'kingshot_ru' || gid === '20235' || gid === '20349'
    },
    /** Banner / 弹窗浮层宝箱图：GOT → got_gift.png，GOF → gift.png */
    giftChestSrc() {
      return this.isGotTaskRewardsTheme ? gotGiftPng : giftPngDefault
    },
    /** 弹窗内固定文案，集中一处避免多个小 computed */
    dialogText() {
      /** 有 i18n key 则翻译，否则用 fb 兜底 */
      const pick = (key, fb) => (this.$te(key) ? this.$t(key) : this.$t(key))
      return {
        title: pick('task.rewards_title', ''),
        guestFoot: pick('task.login_tips', ''),
        goToBtn: pick('task.btn_goto', ''),
        completedLbl: pick('task.txt_completed', ''),
        footPlain: this.isLogin ? pick('task.tips', '') : pick('task.login_tips', ''),
      }
    },
    /** 与 Vuex user.isLogin 一致 */
    taskLoggedIn() {
      return !!this.isLogin
    },
    /** 将接口 task_info 转为弹窗任务行；无数据时为空数组 */
    taskRowsForModal() {
      return (this.taskInfoList || []).map((api) => this.buildTaskRow(api))
    },
  },
  watch: {
    /** 接口再次下发红点时，恢复 Banner 宝箱动效 */
    'gameInfo.has_red_dot'(val) {
      if (Number(val) === 1) {
        this.bannerChestDismissed = false
      }
    },
    /** 关闭弹窗后清除弹窗内动效，下次打开不再播放（除非本次打开时仍有红点） */
    taskRewardsVisible(val) {
      if (!val) {
        this.modalTaskHotAnim = false
      }
    },
  },
  /** 事件处理与接口：弹窗、任务行构建、登录/PWA 引导 */
  methods: {
    onBannerAssetLoaded() {
      this.syncBannerAssetsReady()
    },
    syncBannerAssetsReady() {
      if (this.bannerAssetsReady) return
      const isLoaded = (img) => img && img.complete && img.naturalWidth > 0
      const chest = this.$refs.bannerChest
      const glowBg = this.$refs.bannerGlowBg
      const glowLight = this.$refs.bannerGlowLight
      if (isLoaded(chest) && isLoaded(glowBg) && isLoaded(glowLight)) {
        this.bannerAssetsReady = true
      }
    },
    closeTaskRewardsModal() {
      this.taskRewardsVisible = false
    },
    /** PWA 回到前台时，仅弹窗已打开才刷新 /task_info */
    refreshTaskInfoIfModalOpen() {
      if (!this.taskRewardsVisible) return
      return this.fetchTaskInfo()
    },
    /** 点击 Banner 宝箱：Banner 动效/红点立即关；弹窗内动效本次打开保留，关弹窗后清除 */
    async openTaskRewardsModal() {
      const hadBannerHot = this.bannerChestHot
      if (hadBannerHot) {
        this.modalTaskHotAnim = true
        this.bannerChestDismissed = true
        this.$emit('clear-rewards-red-dot')
      }
      this.trackVipmallTask('1')
      this.taskRewardsVisible = true
      await this.fetchTaskInfo()
      this.trackVipmallTask('2')
    },
    /**
     * 任务相关埋点：未登录不传 fake_id；已登录用 gameInfo.role_id
     */
    trackVipmallTask(action, task_type) {
      const payload = {
        operator_type: action,
      }
      const rid = this.gameInfo && this.gameInfo.role_id
      if (this.isLogin && rid) payload.fake_id = rid
      if (action === 3) {
        payload.task_type = task_type
      }
      console.log('action', payload)
      this.$ta.track('vipmall_task', payload)
    },
    onPrizesDragStart(e) {
      if (e.button !== 0 || this.isMobile) return
      const el = e.currentTarget
      if (el.scrollWidth <= el.clientWidth) return
      const startX = e.pageX
      const startScroll = el.scrollLeft
      const onMove = (ev) => {
        el.scrollLeft = startScroll - (ev.pageX - startX)
      }
      const onUp = () => {
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
      }
      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
    },
    onPrizesTouchStart(e) {
      if (e.touches.length !== 1) return
      const t = e.touches[0]
      this._prizesTouch = { x: t.clientX, y: t.clientY, axis: null }
    },
    onPrizesTouchMove(e) {
      const s = this._prizesTouch
      if (!s || e.touches.length !== 1) return
      const t = e.touches[0]
      const dx = t.clientX - s.x
      const dy = t.clientY - s.y
      if (!s.axis) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return
        s.axis = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y'
      }
      if (s.axis === 'x') e.stopPropagation()
    },
    onPrizesTouchEnd() {
      this._prizesTouch = null
    },
    /** 请求 /task_info；已登录传 role_id，成功时写入 taskInfoList */
    async fetchTaskInfo() {
      this.taskInfoLoading = true
      try {
        const role_id = this.gameInfo?.role_id || ''
        const real_role_id = this.gameInfo?.real_role_id || ''
        const res = await taskInfo({
          game_id: this.gameId,
          from_desktop_app: isEnteredFromInstalledWebApp() ? 1 : 0,
          real_role_id,
          role_id,
        })
        const ok = res.data && Number(res.data.code) === 1
        const raw = ok && res.data.data && res.data.data.task_info
        this.taskInfoList = Array.isArray(raw) ? raw : []
      } catch (e) {
        this.taskInfoList = []
      } finally {
        this.taskInfoLoading = false
      }
    },
    /**
     * 后端 completed_time：常见为秒级 unix、毫秒时间戳或非纯数字字符串；统一为日历日 YYYY.MM.DD
     * @param {string|number} val 接口返回值
     * @returns {string}
     */
    formatTaskCompletedDate(val) {
      const s = `${val}`.trim()
      if (!s) return ''
      const n = Number(s)
      if (Number.isFinite(n) && n > 0) {
        /** >= 1e11 视作毫秒（约 5138-09 的秒上限），否则按秒 unix */
        const d = n >= 1e11 ? dayjs(n) : dayjs.unix(n)
        return d.isValid() ? d.format('YYYY.MM.DD') : ''
      }
      const d = dayjs(s)
      return d.isValid() ? d.format('YYYY.MM.DD') : ''
    },
    /**
     * 将单条 task_info 转为表格行数据（标题、进度、奖励 chip、是否完成、完成日期）
     * @param {Object} api 接口 task_info 数组元素
     */
    buildTaskRow(api) {
      const task_key = api.task_key
      const titleMain = api.task_text
      const cc = Number(api.completed_count)
      const tc = Number(api.total_count)
      const titleProgress = ` (${cc}/${tc})`
      const chips = this.chipsFromPackage(api.task_package_info)
      const completed = Number(api.completed) === 1
      let completedDate = ''
      if (completed && api.completed_time != null && `${api.completed_time}`.trim() !== '') {
        completedDate = this.formatTaskCompletedDate(api.completed_time)
      }
      return {
        task_key,
        titleMain,
        titleProgress,
        chips,
        completed,
        completedDate,
      }
    },
    /** 仅 login / desktop_app_login 展示 GO TO 按钮；未登录不展示 */
    shouldShowTaskRowGo(task_key) {
      if (!this.taskLoggedIn) return false
      return task_key !== 'cumulative_login'
    },
    /**
     * 任务行主标题（webshop_lang：task_rewards_task1_{gameId} / task_rewards_task2_{gameId}）
     * @param {string} task_key login | desktop_app_login
     */
    taskTitleBaseForRow(task_key) {
      if (task_key === 'login') {
        const i18nKey = `task.rewards_task2_${this.gameId}`
        return this.$t(i18nKey)
      }
      if (task_key === 'desktop_app_login') {
        const i18nKey = `task.rewards_task1_${this.gameId}`
        return this.$t(i18nKey)
      }
      return ''
    },
    /**
     * 将接口 task_package_info 转为弹窗内 chip：有图则 url+count，否则纯文案 label
     * @param {Array} list task_info[].task_package_info
     */
    chipsFromPackage(list) {
      const arr = Array.isArray(list)
        ? list
        : list && typeof list === 'object' && Array.isArray(list.other_content)
          ? list.other_content
          : []
      if (arr.length === 0) return []
      return arr.map((it) => {
        const url = it.content || ''
        const count = it.count || 0
        if (url) {
          return { url: String(url), count: Number(count) || 0 }
        }
      })
    },

    /** 底部「登录」：关弹窗，必要时先走 Cookie 同意再调父级 setShowLoginModal */
    onTaskLoginGo() {
      this.taskRewardsVisible = false
      this.$parent.setShowLoginModal(true)
    },
    /**
     * 已完成行的「GO TO」：login 跳转游戏 scheme；desktop_app_login 走 PWA 安装/加桌引导
     * @param {Object} row buildTaskRow 返回的行
     */
    onTaskRowGo(row) {
      if (!row) return
      if (row.task_key === 'login') {
        if (!this.taskLoggedIn) {
          this.trackVipmallTask(3, 1)
          this.onTaskLoginGo()
          return
        }
        this.taskRewardsVisible = false
      }
      if (row.task_key === 'desktop_app_login') {
        this.trackVipmallTask(3, 2)
        this._onTaskPwaGoTo()
      }
    },
    /** PWA 桌面任务 GO：先关任务浮层再打开 head/webapp 加桌引导（避免挡住 Dialog） */
    _emitOpenPwaAddToHomeGuideFromTask() {
      this.taskRewardsVisible = false
      this.$nextTick(() => {
        this.$root.Bus.$emit('openPwaAddToHomeGuide')
      })
    },
    /** PWA 桌面任务 GO：Chrome 系列走安装提示状态；苹果触控设备走加桌引导图（含 iOS Safari） */
    _onTaskPwaGoTo() {
      // 如果可以通过api添加桌面应用
      if (isPwaTaskBeforeInstallProbeUa()) {
        // 如果没安装过
        if (this.isInstallPromptSupported) {
          this._emitOpenPwaAddToHomeGuideFromTask()
        } else {
          this.$toast(this.$t('task.pwaLoginGuide'))
        }
        return
      }

      if (this.isMobile) {
        this._emitOpenPwaAddToHomeGuideFromTask()
      } else {
        this.$toast(this.$t('task.pwaBroswerTips'))
      }
    },
  },
}
</script>

<style scoped>
/* ========== 弹窗内：任务列表、扫光、印章、GO TO、页脚（scoped） ========== */
.bottom {
  display: flex;
  justify-content: space-between;
}

.task-rewards-modal {
  max-height: 70vh;
  overflow-y: auto;
}
.row-content {
  min-height: 180px;
  max-height: 340px;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  -ms-overflow-style: none;
  scrollbar-width: none;
  scrollbar-color: transparent transparent;
}

.task-rewards-skeleton {
  padding: 10px 20px 10px 10px;
  border-radius: 8px;
  margin-bottom: 8px;
  background: #f5f7f9;
}

.task-rewards-skeleton__title {
  height: 16px;
  width: 60%;
  border-radius: 4px;
  margin-bottom: 10px;
}

.task-rewards-skeleton__bottom {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.task-rewards-skeleton__prizes {
  width: 176px;
  min-width: 176px;
  height: 64px;
  border-radius: 8px;
}

.task-rewards-skeleton__btn {
  width: 88px;
  height: 36px;
  border-radius: 18px;
}

.task-rewards-row {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 20px 10px 10px;
  border-bottom: 1px solid #eceff1;
  position: relative;
  overflow: hidden;
  opacity: 1;
  border-radius: 8px;
  background: radial-gradient(50% 50% at 50% 50%, #ffffff 0%, #cfe2fa 100%);
  margin-bottom: 8px;
}

.task-rewards-row--got {
  background: radial-gradient(50% 50% at 50% 50%, #f1f1f1 0%, #f6ead7 100%);
}

.task-rewards-row:last-of-type {
  border-bottom: none;
  margin-bottom: 0;
}

.task-rewards-row__main {
  flex: 1;
  min-width: 0;
  position: relative;
}

.task-rewards-row__title,
.bottom {
  position: relative;
  z-index: 1;
}

.task-rewards-done__bg {
  position: absolute;
  width: 108px;
  height: 108px;
  right: -45px;
  bottom: -45px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  pointer-events: none;
  z-index: 0;
}

.task-rewards-modal--hot .task-rewards-done__bg {
  animation: complete-reveal 0.35s ease-out 0.45s both;
}

.task-rewards-row__title {
  font-family: 'roboto-medium';
  font-weight: 500;
  font-size: 14px;
  margin-bottom: 10px;
  color: var(--Gray-80, #343a3f);
  line-height: 16px;
  letter-spacing: -0.1%;
  vertical-align: middle;
}

.task-rewards-row__progress {
  color: #343a3f;
  font-weight: 700;
}

.task-rewards-row__progress--completed {
  color: #2ebc7f;
}

.task-rewards-prizes {
  display: flex;
  width: 176px;
  max-width: 176px;
  min-width: 0;
  flex-shrink: 0;
  min-height: 64px;
  overflow-x: auto;
  overflow-y: hidden;
  flex-wrap: nowrap;
  gap: 6px;
  padding: 7px;
  background: #acc7e8;
  border-radius: 8px;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-x;
  overscroll-behavior-x: contain;
  transform: translateZ(0);
  /* -ms-overflow-style: none; */
  scrollbar-width: none;
}

.task-rewards-prizes::-webkit-scrollbar {
  display: none;
}

@media (hover: hover) and (pointer: fine) {
  .task-rewards-prizes {
    cursor: grab;
    user-select: none;
  }
}

.task-rewards-prizes--wide {
  width: 190px;
  max-width: 190px;
}

.task-rewards-prizes--got {
  background: #e1cdb0;
}

.task-rewards-chip {
  display: inline-flex;
  flex-shrink: 0;
  position: relative;
  align-items: center;
  height: 50px;
  width: 50px;
  font-weight: 600;
  border-radius: 6px;
  padding: 4px 8px;
}

.task-rewards-chip--icon {
  padding: 0;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
}

.task-rewards-chip__count {
  position: absolute;
  right: 6px;
  bottom: 6px;
  display: inline-block;
  font-family: roboto-bold;
  font-size: 10px;
  line-height: 1;
  letter-spacing: -0.1%;
  text-align: right;
  white-space: nowrap;
  color: #fff;
  isolation: isolate;

  &::before {
    content: attr(data-text);
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    font: inherit;
    letter-spacing: inherit;
    text-align: inherit;
    white-space: inherit;
    pointer-events: none;
    z-index: -1;
    color: transparent;
    -webkit-text-stroke: 4px #343a3f;
    paint-order: stroke fill;
  }
}

.task-rewards-row__side {
  flex-shrink: 0;
  width: 88px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  position: relative;
}

.task-rewards-row__completeSweep {
  position: absolute;
  inset: 0;
  z-index: 5;
  pointer-events: none;
  overflow: hidden;
}

.task-rewards-modal--hot .task-rewards-row__completeSweep-inner {
  position: absolute;
  top: -10%;
  left: -42%;
  width: 42%;
  height: 120%;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.72;
  filter: blur(0.25px);
  transform: skewX(-12deg);
  transform-origin: center center;
  animation: task-rewards-complete-sweep 0.5s ease-out forwards;
}

@keyframes task-rewards-complete-sweep {
  0% {
    left: -42%;
  }
  100% {
    left: 100%;
  }
}

.task-rewards-seal {
  position: relative;
  width: 100%;
  min-height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.task-rewards-seal__stamp {
  position: relative;
  z-index: 2;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.task-rewards-seal__text {
  position: relative;
  z-index: 1;
  padding: 2px 4px 0;
}

.task-rewards-seal__text .task-rewards-done__date {
  color: #2ebc7f;
}

.task-rewards-modal--hot .task-rewards-seal--play .task-rewards-seal__stamp {
  animation: task-rewards-stamp-in 0.85s cubic-bezier(0.33, 1.55, 0.52, 1) both;
}

@keyframes task-rewards-stamp-in {
  0% {
    opacity: 0;
    transform: scale(1.6);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

.task-rewards-done__text {
  font-size: 13px;
  font-weight: 700;
  color: #2ebc7f;
}

.task-rewards-done__date {
  font-size: 14px;
  line-height: 15px;
  color: #7a8699;
}

.task-rewards-modal--hot .task-rewards-done__text,
.task-rewards-modal--hot .task-rewards-done__date {
  opacity: 0;
  visibility: hidden;
  animation: complete-reveal-two 0.35s ease-out 0.35s both;
}

.task-rewards-modal--hot .task-rewards-seal--play .task-rewards-done__text,
.task-rewards-modal--hot .task-rewards-seal--play .task-rewards-done__date {
  animation: task-rewards-done-text-in 0.6s ease-out 1s both;
}

@keyframes task-rewards-done-text-in {
  from {
    opacity: 0;
    visibility: hidden;
    transform: scale(2.2);
  }
  to {
    opacity: 1;
    visibility: visible;
    transform: scale(1);
  }
}

.task-rewards-go {
  font-size: 16px;
  width: 91px;
  height: 34px;
  line-height: 34px;
  background: #1bc263;
  font-weight: 700;
  color: #fff;
  text-align: center;
  border-radius: 8px;
  cursor: pointer;
  border: none;
}

@keyframes complete-reveal {
  from {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
  }
  to {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }
}

@keyframes complete-reveal-two {
  0% {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
  }
  10% {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transform: scale(2.2);
  }
  100% {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transform: scale(1);
  }
}

.task-rewards-foot {
  font-size: 12px;
  margin-top: 8px;
  line-height: 1.45;
  color: #7a8699;
  text-align: center;
}

.task-rewards-foot-em {
  color: #2ebc7f;
  font-weight: 600;
}

.task-rewards-login-wide {
  display: block;
  width: 100%;
  margin-top: 12px;
  padding: 12px 16px;
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  text-align: center;
  background: #1bc263;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(46, 188, 127, 0.35);
}
</style>

<style lang="less">
/* ========== 全局：Banner 宝箱 + 弹层上方 reward-task（非 scoped，与 game.css 协同） ========== */
/* 与 game.css 原逻辑一致；入口在 Dialog 外，用 display:contents 保持 .gameback 子节点布局 */
.game-task-rewards-root {
  display: contents;
}

/* Banner 右侧奖励：单宝箱 + 光效 + 文案（动效由 has_red_dot 切换） */
.gameback-rewards {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  pointer-events: auto;
}

/* PC（game.vue 上 .gameback.pc）：宝箱略向左上，避免贴边过重 */
.gameback.pc .gameback-rewards {
  right: 20%;
  top: 45px;
}

.gameback-rewards__chest-area {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 60px;
  min-height: 62px;
}

.gameback-rewards__chest-wrap .gameback-rewards__glow-stack {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 76px;
  height: 76px;
  margin: -38px 0 0 -38px;
  pointer-events: none;
  z-index: 0;
  filter: saturate(1);
}

.gameback-rewards__glow-bg {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 76%;
  height: 76%;
  transform: translate(-50%, -50%);
  object-fit: contain;
  display: block;
  filter: brightness(1.06);
}

.gameback-rewards__glow-light {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 101%;
  height: 101%;
  transform: translate(-50%, -50%);
  object-fit: contain;
  display: block;
  opacity: 0.78;
  filter: brightness(1.04);
}

.gameback-rewards__chest-wrap--anim-ready .gameback-rewards__glow-bg {
  animation: gameback-rewards-glow-bg-pulse 3.2s ease-in-out infinite;
}

.gameback-rewards__chest-wrap--anim-ready .gameback-rewards__glow-light {
  animation: gameback-rewards-glow-light-spin 8s linear infinite;
}

@keyframes gameback-rewards-glow-light-spin {
  from {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

@keyframes gameback-rewards-glow-bg-pulse {
  0%,
  100% {
    opacity: 0.54;
    transform: translate(-50%, -50%) scale(1);
  }
  50% {
    opacity: 0.7;
    transform: translate(-50%, -50%) scale(1.02);
  }
}

.gameback-rewards__chest-wrap {
  position: relative;
  width: 60px;
  height: 62px;
  display: flex;
  align-items: center;
  justify-content: center;
  transform-origin: 50% 80%;
}

.gameback-rewards__chest-wrap--anim-ready {
  animation: gameback-rewards-chest-shake 3s ease-in-out infinite;
}

.gameback-rewards__chest-wrap--anim-ready.gameback-rewards__chest-wrap--hot {
  animation: gameback-rewards-chest-hot 1s linear infinite;
}

.gameback-rewards__chest-wrap--hot {
  transform-origin: 50% 85%;
}

.gameback-rewards__chest {
  position: relative;
  z-index: 1;
  width: 51px;
  height: 44.8px;
  object-fit: contain;
  display: block;
  width: 83% !important;
}

.gameback-rewards__chest--got {
  transform: scale(1.2) translateY(-3px);
}

.gameback-rewards__red-dot {
  position: absolute;
  top: 6px;
  right: 5px;
  z-index: 3;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #f13e41;
  box-shadow: 0px 0.5px 0px 0px #7c0202;
  pointer-events: none;
}

@keyframes gameback-rewards-chest-hot {
  0% {
    transform: translateY(0) rotate(0deg);
  }
  5% {
    transform: translateY(-0.99px) rotate(-2.43deg);
  }
  10% {
    transform: translateY(-1.98px) rotate(2.43deg);
  }
  15% {
    transform: translateY(-2.97px) rotate(-3.24deg);
  }
  20% {
    transform: translateY(-3.96px) rotate(3.24deg);
  }
  24% {
    transform: translateY(-3.96px) rotate(-8.91deg);
  }
  30% {
    transform: translateY(-3.96px) rotate(8.91deg);
  }
  35% {
    transform: translateY(-3.96px) rotate(-8.1deg);
  }
  41% {
    transform: translateY(-3.96px) rotate(8.1deg);
  }
  46% {
    transform: translateY(-3.96px) rotate(-7.29deg);
  }
  52% {
    transform: translateY(-3.96px) rotate(7.29deg);
  }
  58% {
    transform: translateY(-3.96px) rotate(0deg);
  }
  76% {
    transform: translateY(-3.96px) rotate(0deg);
  }
  84% {
    transform: translateY(0) rotate(0deg);
  }
  100% {
    transform: translateY(0) rotate(0deg);
  }
}

@keyframes gameback-rewards-chest-shake {
  0% {
    transform: rotate(0deg);
  }
  3% {
    transform: rotate(-1.8deg);
  }
  6% {
    transform: rotate(1.8deg);
  }
  9% {
    transform: rotate(-1.2deg);
  }
  12% {
    transform: rotate(1.2deg);
  }
  15% {
    transform: rotate(-0.7deg);
  }
  18% {
    transform: rotate(0.5deg);
  }
  20%,
  100% {
    transform: rotate(0deg);
  }
}

.gameback-rewards__label {
  position: absolute;
  max-width: 70px;
  bottom: 7px;
  z-index: 3;
  text-align: center;
  font-family: roboto-bold;
  font-weight: 700;
  font-size: 13.3px;
  color: #fff;
  word-break: break-word;

  &::before,
  &::after {
    content: attr(data-text);
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    pointer-events: none;
  }

  &::before {
    z-index: -1;
    color: transparent;
    -webkit-text-stroke: 1.5px #343a3f;
    paint-order: stroke fill;
  }

  &::after {
    z-index: -2;
    color: transparent;
    text-shadow:
      0 1.5px 0 #343a3f,
      0 1.5px 0 #2a3035;
  }
}

.reward-task {
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 5px;
  pointer-events: none;
}

.reward-task__chest-area {
  position: relative;
  flex-shrink: 0;
  width: 52px;
  height: 54px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.reward-task__glow-stack {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 66px;
  height: 66px;
  margin: -33px 0 0 -33px;
  pointer-events: none;
  z-index: 0;
}

.reward-task__glow-bg {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 76%;
  height: 76%;
  transform: translate(-50%, -50%);
  object-fit: contain;
  display: block;
}

.reward-task__glow-light {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 101%;
  height: 101%;
  object-fit: contain;
  display: block;
  opacity: 0.78;
  animation: reward-task-glow-light-spin 8s linear infinite;
}

@keyframes reward-task-glow-light-spin {
  from {
    transform: translate(-50%, -50%) scale(1.2) rotate(0deg);
  }
  to {
    transform: translate(-50%, -50%) scale(1.2) rotate(360deg);
  }
}

.reward-task__chest {
  position: relative;
  z-index: 1;
  width: 44px;
  height: auto;
  object-fit: contain;
  display: block;
}

.reward-task__chest--got {
  transform: scale(1.2) translateY(-3px);
}

.reward-task__label {
  margin: 0;
  flex: 1;
  min-width: 0;
  font-family: roboto-bold, Roboto, sans-serif;
  font-weight: 700;
  font-size: 13px;
  line-height: 1.25;
  color: #fff4c4;
  -webkit-text-stroke-color: 0.45px #343a3f;
  text-shadow: 0 0.35px 0 #343a3f;
  word-break: break-word;
}

/* row-content 滚动条隐藏（放全局样式，避免 scoped 对 ::-webkit-scrollbar 不生效） */
.task-rewards-float-dialog__body .row-content {
  -ms-overflow-style: none;
  scrollbar-width: none;
  scrollbar-color: transparent transparent;
}

.task-rewards-float-dialog__body .row-content::-webkit-scrollbar {
  -webkit-appearance: none;
  display: none;
  width: 0 !important;
  height: 0 !important;
  background: transparent;
}

.task-rewards-float-dialog__body .row-content::-webkit-scrollbar-thumb,
.task-rewards-float-dialog__body .row-content::-webkit-scrollbar-track {
  display: none;
  background: transparent;
}
</style>
